/**
 * Pure-функция расчёта стоимости генерации в кредитах для модели + настроек.
 *
 * Source of truth для:
 *   1. useGenerateCost — реалтайм-цена на /generate (selectedModel + UI settings)
 *   2. PopularModelCard (HomeTemp / PopularModelsTab) — цена на карточке
 *      popular_category (settings заданы админом)
 *
 * Без этой функции card-list показывал бы `realModel.cost` (= минимум по
 * вариантам — `min_credits_price`), а на /generate юзер видел бы другую цифру
 * (правильную с учётом resolution/duration). DRY → одна формула, не разъедется
 * при будущих изменениях pricing-логики.
 *
 * Возвращает количество кредитов (Math.round'нутое). 0 для model = null.
 */
import type { Model } from '../data/models'
import {
	GAMMA_IMAGE_MODEL_OPTIONS,
	GAMMA_IMAGE_TIER_DEFAULT_SURCHARGE,
} from '../pages/generate/utils/gammaOptions'
import type { AudioOperation } from '../context/GenerateContext'
import { isKling4Model, isWan3Model } from './frameRefModes'

/** Маппинг audio-операции → variant-key модели Suno.
 *  'generate' идёт по обычному resolution/speed-флоу ниже; остальные — direct
 *  lookup по variant-ключу (extend/cover/+vocals/+instr). */
const AUDIO_OPERATION_VARIANT_KEY: Record<AudioOperation, string | null> = {
	generate: null,
	extend: 'extend',
	cover: 'upload_cover',
	add_vocals: 'add_vocals',
	add_instrumental: 'add_instrumental',
}

/** Настройки влияющие на цену — пересечение GenerateSettings (camelCase) и
 *  PopCardSettings (тоже camelCase). Обе стороны передают сюда напрямую. */
/** Цена Gamma-деки в кредитах — ОДНА формула на фронте (фаза 0, 31.08);
 * зеркалит бэк gamma_credits_price: max(n × (per_slide + надбавка тира), min). */
export function gammaCreditsPrice(numCards: number, perSlide: number, minPrice: number, surcharge = 0): number {
	return Math.max(Math.max(1, numCards) * (perSlide + surcharge), minPrice)
}

/** Зеркало правила get_video_credits_price (video.py): ключ, чья часть до
 *  суффикса `_text|_video|_with_sound|_no_sound` — разрешение вида `720p`,
 *  бэк считает ПОСЕКУНДНЫМ и домножает на длительность — независимо от флага
 *  is_per_second в price_variants (у прямого Kling его нет). Без зеркала
 *  витрина показывала бы цену одной секунды за весь ролик. */
export function isPerSecondKey(key: string): boolean {
	const resPart = key.replace(/_(text|video|with_sound|no_sound)$/, '')
	return /^\d+p$/.test(resPart)
}

export interface ModelCostSettings {
	resolution?: string | null
	duration?: string | null
	sound?: boolean
	speed?: string | null
	quality?: string | null
	presentation?: {
		gammaNumCards?: number
		gammaImageModel?: string
		gammaImageSource?: string
	}
}

/** Контекст: extra-параметры из флоу /generate. Карточки popular_category
 *  передают пустой объект — у них нет загруженного видео/audioOperation. */
export interface ModelCostContext {
	requiresVideo?: boolean
	selectedVideoDuration?: number
	uploadedVideoDuration?: number
	audioOperation?: AudioOperation
	audioSubmode?: 'music' | 'sounds' | 'soundtrack' | 'tts'
	presentationEstimate?: number | null
	hasImage?: boolean
	hasVideo?: boolean
	/** Сколько фото уйдёт в запрос (getImageInput): за каждое — надбавка модели, см. photoSurcharge. */
	imageCount?: number
	/** Секунды видео на входе к оплате (billableInputSeconds по замеру сервера) — у моделей с платой за вход
	 *  (model.inputVideoBilling). null/undefined — не измерены: цену со входом не считаем (inputVideoPending). */
	inputVideoSeconds?: number | null
}

/** Ставка «вход + результат» за секунду (строка «<разрешение>_video_io») — когда плата за секунды входного видео
 *  включена у модели (выключатель «Правила цены», backend services/input_video_billing), иначе null: цена как раньше. */
export function inputVideoRate(
	model: Model | null | undefined,
	settings: { resolution?: string | null } | null | undefined,
): number | null {
	if (!model?.inputVideoBilling || model.category !== 'video') return null
	// Плату за вход берут Seedance 2.x, Wan 3.0 и Kling 4.0 (backend input_video_billing.eligible_model).
	const id = (model.backendModel || model.id || '').toLowerCase()
	if (!id.includes('seedance-2') && !isWan3Model(model) && !isKling4Model(model)) return null
	const res = settings?.resolution?.toLowerCase()
	if (!res) return null
	const price = model.variants?.find((v) => v.key === `${res}_video_io`)?.credits_price
	return price != null && price > 0 ? price : null
}

/** Карточка тренда с видео (видео карточки — вход у Seedance 2.x в «Референсах»): контекст цены плитки при плате за
 *  секунды входного видео. Секунды видео тренда меряет сервер (reference_video_seconds в выдаче витрины); нет — цена
 *  плитки как раньше (точную цену до запуска покажет виджет). */
export function cardInputVideoContext(
	model: Model | null | undefined,
	card: { has_reference_video?: boolean; reference_video_seconds?: number | null; settings?: unknown },
): ModelCostContext {
	if (!card.has_reference_video || inputVideoRate(model, card.settings as { resolution?: string | null }) == null) return {}
	if (((card.settings as { seedanceMode?: string } | null)?.seedanceMode ?? 'frames') !== 'references') return {}
	const seconds = billableInputSeconds([card.reference_video_seconds ?? null])
	return seconds == null ? {} : { hasVideo: true, inputVideoSeconds: seconds }
}

/** Секунды входа к оплате — то же правило, что у сервера (input_video_billing.billable_seconds): сумма длительностей
 *  вверх до целого с допуском 0,25 с, хоть одна не измерена — null. */
export function billableInputSeconds(seconds: (number | null | undefined)[]): number | null {
	if (!seconds.length || seconds.some((s) => s == null || !(s > 0))) return null
	const total = (seconds as number[]).reduce((a, b) => a + b, 0)
	return Math.max(1, Math.ceil(total - 0.25))
}

/** Секунды входа в цене — зеркало backend input_video_billing.billed_input_seconds: у Kling 4.0 вход короче итога
 *  оплачивается как итог (у Kling «итог × 2»), у остальных — сколько есть. */
export function billedInputSeconds(
	model: { id?: string; backendModel?: string } | null | undefined,
	inputSeconds: number,
	outputSeconds: number,
): number {
	return isKling4Model(model) ? Math.max(inputSeconds, outputSeconds) : inputSeconds
}

/** Надбавка за входные фото — вариант модели с per_input_image (строка «input_photo» в админке): цена на кнопке
 *  = база за качество + надбавка × число приложенных фото, ровно как спишет бэк (images.get_credits_price,
 *  владелец 08.10: у OpenAI каждое входное фото платное). У моделей без такой строки — 0; в «от N» не входит. */
export function photoSurcharge(model: Model | null | undefined, imageCount: number | undefined): number {
	const n = Math.floor(imageCount ?? 0)
	if (!model || !(n > 0)) return 0
	const per = model.variants?.find((v) => v.per_input_image || v.key === 'input_photo')?.credits_price
	if (per == null || !(per > 0)) return 0
	return Math.round(per * n)
}

export function computeModelCost(
	model: Model | null | undefined,
	settings: ModelCostSettings | null | undefined,
	context: ModelCostContext = {},
): number {
	const base = computeBaseModelCost(model, settings, context)
	// Фото на входе платные только у картинок (видео-референсы считаются своими вариантами).
	return model?.category === 'image' ? base + photoSurcharge(model, context.imageCount) : base
}

function computeBaseModelCost(
	model: Model | null | undefined,
	settings: ModelCostSettings | null | undefined,
	context: ModelCostContext = {},
): number {
	if (!model) return 0
	const s = settings ?? {}
	const {
		requiresVideo = false,
		selectedVideoDuration = 5,
		uploadedVideoDuration,
		audioOperation = 'generate',
		audioSubmode,
		presentationEstimate = null,
		hasVideo = false,
		inputVideoSeconds = null,
	} = context

	// Presentation — отдельная ветка: цена динамическая.
	//   • Gamma: max(numCards × per_slide, min_price), variants управляются админом.
	//   • Umnik Slides (Claude): /presentations/estimate (приходит из родителя).
	//     Пока estimate не пришёл — показываем base cost модели.
	if (model.category === 'presentation') {
		if (model.backendModel === 'gamma-presentation') {
			const numCards = s.presentation?.gammaNumCards ?? 5
			const perSlide =
				model.variants?.find((v) => v.key === 'per_slide')?.credits_price ?? 25
			const minPrice =
				model.variants?.find((v) => v.key === 'min')?.credits_price ?? 200
			// Платный тир модели картинок — надбавка за каждый слайд (только
			// при AI-источнике). Формула зеркалит бэк gamma_credits_price.
			let surcharge = 0
			const imageSource = s.presentation?.gammaImageSource ?? 'aiGenerated'
			const imageModel = s.presentation?.gammaImageModel ?? ''
			if (imageSource === 'aiGenerated' && imageModel) {
				const tier =
					GAMMA_IMAGE_MODEL_OPTIONS.find((o) => o.value === imageModel)?.tier ?? 'ultra'
				surcharge =
					model.variants?.find((v) => v.key === `img_${tier}`)?.credits_price ??
					GAMMA_IMAGE_TIER_DEFAULT_SURCHARGE[tier] ??
					0
			}
			return gammaCreditsPrice(numCards, perSlide, minPrice, surcharge)
		}
		return presentationEstimate ?? model.cost ?? 0
	}

	if (!model.variants?.length) return model.cost || 0

	// Soundtrack (фото/видео→музыка) — отдельный variant-ключ, цена выше базовой
	// генерации. Только audio-модели с этим вариантом (Mureka).
	if (model.category === 'audio' && audioSubmode === 'soundtrack') {
		const variant = model.variants.find((v) => v.key === 'soundtrack')
		if (variant?.credits_price != null) return variant.credits_price
		return model.cost || 0
	}

	// Озвучка (TTS, ElevenLabs) — variant-ключ 'tts', цена фиксированная
	// (≤500 символов).
	if (model.category === 'audio' && audioSubmode === 'tts') {
		const variant = model.variants.find((v) => v.key === 'tts')
		if (variant?.credits_price != null) return variant.credits_price
		// Нет варианта — база модели: её же берёт бэк (elevenlabs_tts.get_price).
		if (model.defaultCost) return model.defaultCost
		// Fallback обязан совпадать со списанием бэка, иначе витрина обманет:
		// политика наценки от ПОТОЛКА длины — 500 символов × $0.08/1000 = $0.04 → 29.
		return 29
	}

	// Audio в параметрической операции (extend/cover/+vocals/+instr) — direct
	// variant lookup. Fallback на базовую цену модели.
	if (model.category === 'audio' && audioOperation !== 'generate') {
		const variantKey = AUDIO_OPERATION_VARIANT_KEY[audioOperation]
		if (variantKey) {
			const variant = model.variants.find((v) => v.key === variantKey)
			if (variant?.credits_price != null) {
				return variant.credits_price
			}
		}
		return model.cost || 0
	}

	// Строим упорядоченный список variant-ключей для перебора. Первый match
	// в model.variants определяет цену. quality → специфичные → стандартные.
	const keys: string[] = []

	// Модель с обязательным видео (Motion Control): длительность ролика — это
	// обрезка референса ползунком, а не пилюля «Длительность». В запрос уходит
	// selectedVideoDuration (generatePayloadBuilders), бэк домножает на него
	// посекундную ставку — витрина обязана считать ту же секунду, иначе цена
	// стоит на месте, пока человек двигает ползунок (тестировщик 21.09).
	const videoDrivenDuration =
		requiresVideo && uploadedVideoDuration ? selectedVideoDuration : null

	if (model.category === 'video') {
		const dur = videoDrivenDuration != null ? String(videoDrivenDuration) : s.duration || '5'
		const snd = s.sound ? 'with_sound' : 'no_sound'
		const sndAudio = s.sound ? 'with_audio' : 'no_audio'
		const mode = s.resolution === '1080p' ? 'pro' : 'standard'
		// `{res}_{with|no}_sound` — форма, которую get_video_credits_price
		// проверяет ПЕРВОЙ (прямой Kling 3.0 / Omni: у них озвученная секунда
		// дороже, и ключ по одной длительности перехватил бы цену без звука).
		if (s.resolution) {
			keys.push(`${s.resolution.toLowerCase()}_${snd}`)
		}
		keys.push(`${mode}_${sndAudio}`)
		keys.push(`${dur}s_${snd}`)
		if (s.resolution) {
			keys.push(`${dur}s_${s.resolution.toLowerCase()}`)
		}
		keys.push(`${dur}s_standard`)
		keys.push(`${dur}s`)
	}

	if (s.resolution) {
		keys.push(s.resolution.toLowerCase())
		keys.push(s.resolution)
	}

	if (s.speed) {
		keys.push(s.speed.toLowerCase())
		keys.push(s.speed)
	}

	keys.push('standard')

	if (s.quality) {
		keys.unshift(s.quality.toLowerCase())
	}

	const isSeedance2 = (model.backendModel || model.id || '')
		.toLowerCase()
		.includes('seedance-2')
	if (isSeedance2 && s.resolution && hasVideo) {
		keys.unshift(`${s.resolution.toLowerCase()}_video`)
	}

	const isVeo31 = (model.backendModel || model.id || '')
		.toLowerCase()
		.includes('veo-3.1')
	if (isVeo31 && !s.sound) {
		keys.unshift(`${s.duration || '5'}s_no_audio`)
	}

	const effectiveDuration = videoDrivenDuration ?? parseInt(s.duration || '5', 10)

	// Плата за секунды входного видео: цена = ставка «вход + результат» × (секунды результата + секунды входа) — ровно как
	// спишет сервер (video.generate_video_async); секунды входа — замер сервера, пока их нет, цену не показываем.
	const ioRate = hasVideo ? inputVideoRate(model, s) : null
	if (ioRate != null && inputVideoSeconds != null && inputVideoSeconds > 0) {
		return Math.round(ioRate * (effectiveDuration + billedInputSeconds(model, inputVideoSeconds, effectiveDuration)))
	}

	for (const key of keys) {
		// Надбавка за фото — не вариант качества/разрешения, в базу не попадает.
		const variant = model.variants.find((v) => v.key === key && !v.per_input_image)
		if (variant?.credits_price != null) {
			if (variant.is_per_second || isSeedance2 || isPerSecondKey(key)) {
				return Math.round(variant.credits_price * effectiveDuration)
			}
			if (requiresVideo && selectedVideoDuration) {
				return Math.round((variant.credits_price * selectedVideoDuration) / 5)
			}
			return Math.round(variant.credits_price)
		}
	}

	// Fallback: settings админа НЕ матчат ни один variant (например карточка
	// настроила resolution='2K' для модели у которой variants по quality, low/
	// medium/high). Берём defaultCost = credits_price из БД (= то, что админ
	// видит как «50 кредитов»), а НЕ model.cost (= min_credits_price = «от 7»).
	// Без этого «Улучшить качество фото» (settings={resolution:'2K'}) показывало
	// 7 ⭐ на сайте при админских 50 кредитах.
	//
	// NB: для video-моделей здесь НЕ умножаем на effectiveDuration. defaultCost =
	// credits_price = уже «полная» цена при дефолтной duration (как админ её
	// настроил). Умножение давало баги вида 170 × 5 = 850 для Kling V3 Motion
	// Control при settings={} (карточка на главной). Если у модели variants
	// per_second — они матчатся выше по resolution и считаются с × duration там.
	const baseCost = model.defaultCost || model.cost || 0
	return Math.round(baseCost)
}
